import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  Zap,
  BarChart2,
  Palette,
  RefreshCw,
  Shield,
  Download,
} from 'lucide-react';

const FeaturesSection = styled.section`
  padding: 6rem 0;
  background: #1f2937;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 10rem 0;
  }
`;

const FeaturesContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 10;

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const FeaturesHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;

  @media (min-width: 768px) {
    margin-bottom: 6rem;
  }
`;

const FeaturesTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;
  color: white;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  span {
    background: linear-gradient(45deg, #7c3aed, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const FeaturesDescription = styled.p`
  font-size: 1.25rem;
  color: white;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (min-width: 640px) {
    font-size: 1.375rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 4rem;
  }
`;

const FeatureCard = styled.div`
  padding: 3rem 2rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.12) 100%
  );
  backdrop-filter: blur(15px);
  border-radius: 2rem;
  position: relative;
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.2),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) =>
    isVisible ? 'translateY(0)' : 'translateY(30px)'};
  transition-delay: ${({ delay }) => delay || '0ms'};

  @media (min-width: 768px) {
    padding: 3.5rem 2.5rem;
  }
`;

const LargeBackgroundIcon = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.1);
  z-index: 1;
  pointer-events: none;

  svg {
    width: 120px;
    height: 120px;
    stroke-width: 1;
    opacity: 0.3;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 10;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const FeatureDescription = styled.p`
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  font-size: 1rem;
  max-width: 90%;
  margin: 0 auto;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

const FeatureBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: ${({ showBadge }) => (showBadge ? 1 : 0)};
  transform: ${({ showBadge }) => (showBadge ? 'scale(1)' : 'scale(0.8)')};
  transition: all 0.3s ease;
  z-index: 20;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const features = [
  {
    icon: <Zap size={24} />,
    title: 'Lightning Fast',
    description:
      'Generate QR codes instantly. No waiting, no complicated steps - just paste your link and go.',
    badge: 'Instant',
  },
  {
    icon: <Palette size={24} />,
    title: 'Custom Design',
    description:
      'Add your logo, choose colors, and customize the design to match your brand perfectly.',
    badge: 'Pro',
  },
  {
    icon: <BarChart2 size={24} />,
    title: 'Analytics',
    description:
      'Track scans, locations, devices, and times. Get insights into how your QR codes perform.',
    badge: 'Insights',
  },
  {
    icon: <RefreshCw size={24} />,
    title: 'Dynamic Links',
    description:
      'Update your destination URL anytime without changing the QR code. Perfect for campaigns.',
    badge: 'Smart',
  },
  {
    icon: <Shield size={24} />,
    title: 'Secure & Reliable',
    description:
      'Enterprise-grade security with 99.9% uptime. Your codes work when you need them.',
    badge: 'Trust',
  },
  {
    icon: <Download size={24} />,
    title: 'Multiple Formats',
    description:
      'Download in PNG, JPG, SVG, or PDF. Perfect quality for both digital and print use.',
    badge: 'Flexible',
  },
];

export default function Features() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <FeaturesSection id="features" ref={sectionRef}>
      <FeaturesContainer>
        <FeaturesHeader>
          <FeaturesTitle>
            Everything You <span>Need</span>
          </FeaturesTitle>
          <FeaturesDescription>
            Powerful features to create, customize, and track your QR codes with
            ease.
          </FeaturesDescription>
        </FeaturesHeader>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              isVisible={isVisible}
              delay={`${index * 100}ms`}
            >
              <LargeBackgroundIcon>{feature.icon}</LargeBackgroundIcon>
              <FeatureBadge showBadge={isVisible}>{feature.badge}</FeatureBadge>

              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesContainer>
    </FeaturesSection>
  );
}
